import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getUserEstateStatus, updateUserEstateStatus, type UpdateUserEstateParams, type UserEstateStatus } from "../api/userEstateStatus";
import { getUserEstate } from "../api/userEstate";
import { toaster } from "baseui/toast";

// Hook to get all available user estate statuses
export const useUserEstateStatuses = () => {
  const { data: statusResponse, isLoading, error } = useQuery({
    queryKey: ['/user_estate_status'],
    queryFn: ({ signal }) => getUserEstateStatus(signal),
    retry: 1,
    enabled: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Extract statuses from response
  let statuses: UserEstateStatus[] = [];
  
  try {
    if (statusResponse?.data?.data && Array.isArray(statusResponse.data.data)) {
      statuses = statusResponse.data.data;
    } else if (statusResponse?.data && Array.isArray(statusResponse.data)) {
      statuses = statusResponse.data;
    } else {
      statuses = [];
    }
  } catch (err) {
    console.error('Error processing status response:', err);
    statuses = [];
  }

  return {
    statuses,
    isLoading,
    error,
  };
};

/**
 * The PUT /user_estate Lambda commits the UPDATE first and only then does the
 * slow part (push notification fan-out), so API Gateway's 29s limit often
 * surfaces as a 504 — or a network timeout with no `response` at all — for a
 * change that DID land. Treat those as "unknown" rather than "failed" and go
 * read the row back to find out.
 */
export const isGatewayTimeout = (error: unknown): boolean => {
  const err = error as { response?: { status?: number }; code?: string } | undefined;
  if (!err) return false;
  if (err.response?.status === 504) return true;
  return !err.response && err.code !== 'ERR_CANCELED';
};

const readBackStatusId = async (params: UpdateUserEstateParams): Promise<number | null> => {
  try {
    const res = await getUserEstate({
      user_id: params.user_id,
      estate_id: params.estate_id,
      limit: 1,
      expand: 'user_estate_status,status_id',
    });
    const rows: any[] = Array.isArray((res as any)?.data) ? (res as any).data : [];
    const row = rows.find((r) => Number(r.estate_id) === Number(params.estate_id)) ?? rows[0];
    if (!row) return null;
    const id = row.status_id ?? row.user_estate_status?.id;
    return id == null ? null : Number(id);
  } catch {
    return null;
  }
};

// Hook to update user estate status
export const useUpdateUserEstateStatus = () => {
  const queryClient = useQueryClient();

  const refreshEstates = () =>
    queryClient.invalidateQueries({ queryKey: ['/user_estate'], exact: false });

  return useMutation({
    mutationFn: (params: UpdateUserEstateParams) => {
      return updateUserEstateStatus(params);
    },
    onSuccess: () => {
      toaster.positive('Estate status updated', {
        autoHideDuration: 3000,
      });
    },
    onError: async (error, variables) => {
      if (isGatewayTimeout(error)) {
        const applied = await readBackStatusId(variables);
        if (applied === variables.status_id) {
          toaster.positive('Estate status updated', { autoHideDuration: 3000 });
          return;
        }
        toaster.warning(
          applied === null
            ? 'The server took too long to answer. Refresh to confirm whether the status changed.'
            : 'The server took too long to answer and the status did not change. Please try again.',
          { autoHideDuration: 6000 },
        );
        return;
      }
      toaster.negative('Failed to update estate status', {
        autoHideDuration: 4000,
      });
    },
    onSettled: () => {
      // Whatever happened, the table must show what the server has now.
      refreshEstates();
    },
  });
};
